import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * BlogPreviewSection - Zeigt die 3 neuesten veröffentlichten Blog-Artikel
 * Daten werden ausschliesslich aus der API geladen, sortiert nach Datum (neueste zuerst)
 */
const BlogPreviewSection = () => {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await fetch(`${API_URL}/api/blog/articles?published_only=true`);
        
        if (!response.ok) {
          throw new Error('Fehler beim Laden der Artikel');
        }
        
        const data = await response.json();
        
        // Nehme die ersten 3 Artikel (bereits nach Datum sortiert vom Backend)
        const latestArticles = (data.articles || []).slice(0, 3);
        setArticles(latestArticles);
        
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadArticles();
  }, []);

  // Während des Ladens nichts anzeigen
  if (isLoading) {
    return (
      <section className="py-24 bg-[#f4f5f3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center py-12">
            <div className="animate-pulse text-[#888b89]">Artikel werden geladen...</div>
          </div>
        </div>
      </section>
    );
  }

  // Bei Fehler oder keine Artikel: Sektion nicht anzeigen
  if (error || articles.length === 0) {
    return null;
  }

  return (
    <section className="py-24 bg-[#f4f5f3]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
          <div>
            <span className="text-[#FF0000] text-sm font-semibold tracking-wider uppercase mb-4 block">
              Blog & Ratgeber
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#1a1c1b] mb-4">
              Aktuelle <span className="text-[#FF0000]">Artikel</span>
            </h2>
            <p className="text-[#5a5d5b] max-w-xl">
              Wissen rund um Unternehmensbewertung, Firmenkauf und -verkauf.
            </p>
          </div>
          <Link 
            to="/blog" 
            className="mt-6 md:mt-0 inline-flex items-center gap-2 px-6 py-3 border-2 border-[#FF0000] text-[#FF0000] font-semibold rounded-full hover:bg-[#FF0000] hover:text-white transition-all"
          >
            Alle Artikel
            <ArrowRight size={18} />
          </Link>
        </div>

        {/* Artikel-Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((article) => (
            <ArticleCard key={article.id} article={article} />
          ))}
        </div>
        
      </div>
    </section>
  );
};

/**
 * ArticleCard - Einzelne Artikel-Karte
 */
const ArticleCard = ({ article }) => {
  const [imageError, setImageError] = useState(false);
  
  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('de-CH');
    } catch {
      return '';
    }
  };

  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col"
    >
      {/* Bild-Bereich */}
      <div className="aspect-video bg-gray-100 relative overflow-hidden">
        {article.image_url && !imageError ? (
          <img 
            src={article.image_url} 
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={() => setImageError(true)}
          />
        ) : (
          // Dezenter Farbverlauf als Fallback - KEIN Buchstabe
          <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200" />
        )}
      </div>

      {/* Inhalt */}
      <div className="p-6 flex flex-col flex-1">
        {/* Kategorie & Datum */}
        <div className="flex items-center justify-between mb-4">
          <span className="px-3 py-1 rounded-full bg-[#FFE5E5] text-[#CC0000] text-xs font-semibold">
            {article.category || 'Allgemein'}
          </span>
          <div className="flex items-center gap-2 text-[#888b89] text-sm">
            <Calendar size={14} />
            <span>{formatDate(article.created_at)}</span>
          </div>
        </div>

        {/* Titel */}
        <h3 className="text-[#1a1c1b] font-bold text-lg mb-3 group-hover:text-[#FF0000] transition-colors line-clamp-2">
          {article.title}
        </h3>

        {/* Beschreibung */}
        <p className="text-[#5a5d5b] text-sm leading-relaxed mb-4 line-clamp-2 flex-1">
          {article.meta_description}
        </p>

        {/* Weiterlesen */}
        <div className="flex items-center gap-2 text-[#FF0000] font-semibold text-sm mt-auto">
          <span>Weiterlesen</span>
          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </div>
      </div>
    </Link>
  );
};

export default BlogPreviewSection;
